import { useDroppable } from "@dnd-kit/core";
import classNames from "classnames";
import find from "lodash/find";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { Flex, Inline } from "PFComponents/containers";
import { DropAreaOverlay } from "PFComponents/drop_area_overlay";
import { DropdownButton } from "PFComponents/dropdown_button";
import { ExperienceLevel } from "PFComponents/experience_level";
import { Typography } from "PFComponents/typography";
import { useProficiencyLevels } from "PFCore/hooks/queries/proficiency_levels/use_proficiency_levels";
import { Experience } from "PFTypes";
import { Dispatch, Fragment, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import { SkillProps } from "../../common/skill";
import { DEFAULT_SORTING_OPTION, useSortingOptions } from "../hooks/use_sorting_options";
import css from "../skills_modal.module.scss";
import { DraggableSkill } from "./draggable_skill";

const experienceToBareTextMap = {
  [Experience.Basic]: "basic",
  [Experience.Developing]: "developing",
  [Experience.Intermediate]: "intermediate",
  [Experience.Proficient]: "proficient",
  [Experience.Advanced]: "advanced"
};

const levelsCountToVariantMap = {
  3: "h5",
  4: "bodyBold",
  5: "bodyBold"
};

type SkillItem = SkillProps["skill"];
type SkillsSorting = Record<string, string>;

type HeaderProps = {
  experience: Experience;
  skills: SkillItem[];
  skillsSortingSelected: SkillsSorting;
  setSkillsSortingSelected: Dispatch<SetStateAction<SkillsSorting>>;
};

const Header = ({ experience, skills, skillsSortingSelected, setSkillsSortingSelected }: HeaderProps) => {
  const { t } = useTranslation("profiles", { keyPrefix: "show.parts" });
  const { data: proficiencyLevels } = useProficiencyLevels();

  const { skillsSortingOptions } = useSortingOptions();
  const experienceLabelMap = useExperienceLabelMap();
  const { experienceLevels } = useExperienceConfig();

  const title = `${experienceLabelMap[experience]} ${skills.length}`;
  const isVerticalLayout = experienceLevels.length > 4;

  return (
    <div className={classNames(css.header, css[experienceToBareTextMap[experience]])}>
      <Inline className={css.title} alignItems="center">
        <Flex
          alignItems={isVerticalLayout ? "start" : "center"}
          direction={isVerticalLayout ? "column-reverse" : "row"}
          className={css.experience}
        >
          <ExperienceLevel
            experience={experience}
            experienceLevels={experienceLevels}
            experienceLabelMap={experienceLabelMap}
            showTooltip={false}
          />
          <Typography
            variant={levelsCountToVariantMap[experienceLevels.length] ?? "h4"}
            title={title}
            noMargin
            clipOverflow
            className={css.experienceText}
          >
            {title}
          </Typography>
        </Flex>
        <DropdownButton
          actionIcon="sort"
          small
          options={skillsSortingOptions}
          handleChange={(item) => {
            setSkillsSortingSelected({
              ...skillsSortingSelected,
              [experience]: item
            });
          }}
          dropdownProps={{
            selectedIndex: skillsSortingOptions.findIndex(
              ({ item }) => item === (skillsSortingSelected[experience] || DEFAULT_SORTING_OPTION)
            )
          }}
          aria-label={t("skillsModal.changeExperienceSorting", {
            experience: experienceLabelMap[experience]
          })}
          className={css.sortIcon}
        />
      </Inline>
      <Typography variant="labelRegular" tag="span" className={css.info}>
        {find(proficiencyLevels, { numericalValue: experience })?.definition || "-"}
      </Typography>
    </div>
  );
};

type SkillsProps = {
  experience: Experience;
  skills: SkillItem[];
  addToLog: (...changes: any[]) => void;
  coreSkillsCount: number;
  developmentalSkillsCount: number;
};

const Skills = ({ experience, skills, addToLog, coreSkillsCount, developmentalSkillsCount }: SkillsProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `droppable-${experience}`,
    data: {
      experience
    }
  });

  return (
    <div ref={setNodeRef} className={classNames(css.skills, css[experienceToBareTextMap[experience]])}>
      {isOver && <DropAreaOverlay zIndex={2} />}
      <div className={css.skillsInner}>
        {skills.map((skill, index) => (
          <DraggableSkill
            key={skill.id}
            skill={skill}
            index={index}
            addToLog={addToLog}
            coreSkillsCount={coreSkillsCount}
            developmentalSkillsCount={developmentalSkillsCount}
          />
        ))}
      </div>
    </div>
  );
};

type SkillsGridProps = {
  skillsByExperience: Record<string, SkillItem[]>;
  skillsSortingSelected: SkillsSorting;
  setSkillsSortingSelected: Dispatch<SetStateAction<SkillsSorting>>;
  addToLog: (...changes: any[]) => void;
  coreSkillsCount: number;
  developmentalSkillsCount: number;
};

const SkillsGrid = ({
  skillsByExperience,
  skillsSortingSelected,
  setSkillsSortingSelected,
  addToLog,
  coreSkillsCount,
  developmentalSkillsCount
}: SkillsGridProps) => {
  const { experienceLevels } = useExperienceConfig();

  return (
    <div
      className={css.skillRatingGrid}
      style={{
        gridTemplateColumns: Array(experienceLevels.length).fill("minmax(0, 1fr)").join(" ")
      }}
    >
      {experienceLevels.map((experience) => (
        <Fragment key={experience}>
          <Header
            key={experience}
            experience={experience}
            skills={skillsByExperience[experience] || []}
            skillsSortingSelected={skillsSortingSelected}
            setSkillsSortingSelected={setSkillsSortingSelected}
          />
          <Skills
            key={experience}
            experience={experience}
            skills={skillsByExperience[experience] || []}
            addToLog={addToLog}
            coreSkillsCount={coreSkillsCount}
            developmentalSkillsCount={developmentalSkillsCount}
          />
        </Fragment>
      ))}
    </div>
  );
};

export default SkillsGrid;
